/**
 * PlaylistSection.tsx
 *
 * Renders upcoming and playlist section variants with desktop/mobile blocks,
 * local visibility controls, and API-based pagination.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getCtSectionTitle } from "../../HeadlessPlaylistSection/headlessPlaylistUtils";
import { LivePlaylistDesktopBlock } from "../../LivePlaylist/LivePlaylistDesktopBlock";
import { LivePlaylistMobileBlock } from "../../LivePlaylist/LivePlaylistMobileBlock";
import { WideCardSkeleton } from "../components/SectionSkeletons";
import { PLAYLIST_VIDEOS_PATH_RE } from "../constants";
import { UnifiedMediaSectionProps } from "../types";

function getLoadingDelayMs(isUpcoming: boolean, initialStreamCount: number): number | null {
  if (!isUpcoming) return 300;
  if (initialStreamCount === 0) return null;
  return 1000;
}

function getHeadingClassName(isUpcoming: boolean, headingSizeClassName: string): string {
  return isUpcoming
    ? `pl-0 font-medium break-words text-[#1A1A1A] ${headingSizeClassName}`
    : `truncate font-medium text-[#1A1A1A] ${headingSizeClassName}`;
}

/**
 * PlaylistSection
 *
 * Handles `mode="upcoming"` and `mode="playlist"` rendering.
 * Provides desktop/mobile blocks, local show-more behavior, and API pagination.
 */
export function PlaylistSection({
  title,
  titleCt,
  data,
  sectionId = "",
  mode,
  setReminderText = "Set Reminder",
  reminderSetText = "Reminder Set",
  enableSuspense = false,
  enableMobileList = false,
  enableShowMore = false,
  increment = 5,
  apiBaseUrl = "",
  influencerId,
  reminderService,
  reduceMobileSectionHeadingSize = false,
}: Readonly<
  Pick<
    UnifiedMediaSectionProps,
    | "title"
    | "titleCt"
    | "data"
    | "sectionId"
    | "mode"
    | "setReminderText"
    | "reminderSetText"
    | "enableSuspense"
    | "enableMobileList"
    | "enableShowMore"
    | "increment"
    | "playListId"
    | "apiBaseUrl"
    | "influencerId"
    | "reminderService"
    | "reduceMobileSectionHeadingSize"
  >
>) {
  const initialStreams = data?.live_streams ?? [];
  const isUpcoming = mode === "upcoming";
  const ctSectionTitle = getCtSectionTitle(
    titleCt,
    title,
    isUpcoming ? "Upcoming Live" : "Live Sessions"
  );

  const effectiveIncrement = isUpcoming ? 3 : increment;
  const effectiveSectionId = isUpcoming ? "upcoming-live-title" : sectionId;

  const headingSizeClassName = reduceMobileSectionHeadingSize
    ? "text-[16px] md:text-[20px]"
    : "text-[20px]";
  const isTrendingSection =
    !isUpcoming && (sectionId === "trending-now-title" || title.toLowerCase().includes("trending"));
  const mobileSectionId = isTrendingSection ? "trending-now-title" : sectionId;
  const showMobileList = isUpcoming || enableMobileList;
  const showShowMoreOnMobile = isUpcoming || enableShowMore;

  const [visibleCount, setVisibleCount] = useState(effectiveIncrement);
  const [loading, setLoading] = useState(true);

  const [allStreams, setAllStreams] = useState(initialStreams);
  const [nextUrl, setNextUrl] = useState<string | null | undefined>(data?.paging?.next);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const fetchedPagesRef = useRef<Set<string>>(new Set());
  const [isFetchingShowMore, setIsFetchingShowMore] = useState(false);
  const isFetchingShowMoreRef = useRef(false);

  useEffect(() => {
    setAllStreams(data?.live_streams ?? []);
    setNextUrl(data?.paging?.next);
  }, [data]);

  useEffect(() => {
    const delayMs = getLoadingDelayMs(isUpcoming, initialStreams.length);
    if (delayMs === null) {
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => setLoading(false), delayMs);
    return () => clearTimeout(timer);
  }, [isUpcoming, initialStreams.length]);

  /**
   * Fetches and appends next page of streams from playlist paging URL.
   */
  const fetchNextPage = useCallback(
    async (currentNextUrl: string): Promise<boolean> => {
      const parsed = (() => {
        try {
          const url = new URL(currentNextUrl, "https://placeholder");
          const page = url.searchParams.get("page");
          const queryPlaylistId = url.searchParams.get("playlistId");
          const pathMatch = PLAYLIST_VIDEOS_PATH_RE.exec(url.pathname);
          const playlistIdFromPath = pathMatch?.[1];
          const playlistId = queryPlaylistId ?? playlistIdFromPath;
          if (!playlistId || !page) return null;

          return `/api/v1/live-streams?playlistId=${encodeURIComponent(playlistId)}&page=${encodeURIComponent(page)}`;
        } catch {
          return null;
        }
      })();

      if (!parsed) return false;

      if (fetchedPagesRef.current.has(currentNextUrl)) {
        setNextUrl(null);
        return true;
      }
      fetchedPagesRef.current.add(currentNextUrl);

      try {
        const res = await fetch(`${apiBaseUrl}${parsed}`, {
          cache: "no-store",
          headers: { Accept: "application/json" },
        });

        if (!res.ok) throw new Error(`Pagination fetch failed: ${res.status}`);

        const json = await res.json();
        const newStreams: typeof initialStreams =
          json?.data?.upcomingliveFromFireworks?.live_streams ?? [];
        const newNext: string | null | undefined =
          json?.data?.upcomingliveFromFireworks?.paging?.next ?? null;

        setAllStreams((prev) => [...prev, ...newStreams]);
        setNextUrl(newNext);
        return true;
      } catch {
        return false;
      }
    },
    [apiBaseUrl]
  );

  const handleScrollEnd = useCallback(async () => {
    if (!nextUrl || isFetchingMoreRef.current || isUpcoming) return;

    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    await fetchNextPage(nextUrl);
    isFetchingMoreRef.current = false;
    setIsFetchingMore(false);
  }, [fetchNextPage, isUpcoming, nextUrl]);

  const handleShowMore = useCallback(async () => {
    if (visibleCount < allStreams.length) {
      setVisibleCount((p) => p + effectiveIncrement);
      return;
    }

    if (!nextUrl || isFetchingShowMoreRef.current) return;
    isFetchingShowMoreRef.current = true;
    setIsFetchingShowMore(true);
    const ok = await fetchNextPage(nextUrl);
    setVisibleCount((p) => (ok ? p + effectiveIncrement : p));
    isFetchingShowMoreRef.current = false;
    setIsFetchingShowMore(false);
  }, [allStreams.length, effectiveIncrement, fetchNextPage, nextUrl, visibleCount]);

  const handleShowLess = useCallback(() => {
    setVisibleCount(effectiveIncrement);
    document
      .getElementById(effectiveSectionId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [effectiveIncrement, effectiveSectionId]);

  const displayTitle = isUpcoming && !title ? "Upcoming Live Sessions" : title;
  const emptyMessage = isUpcoming
    ? "No Data Available for Upcoming Live Sessions"
    : `No ${title} videos found`;
  const sectionClassName = isUpcoming
    ? "px-[16px] md:px-[20px] pb-4"
    : "border-b-2 border-gray-200 px-[16px] pb-[10px] md:px-[24px] md:pb-[12px]";
  const loadingLabel = isUpcoming ? `Loading ${displayTitle} sessions` : `Loading ${title} videos`;

  if (!loading && allStreams.length === 0) {
    return null;
  }

  return (
    <section
      className={sectionClassName}
      aria-labelledby={effectiveSectionId}
      aria-label={isUpcoming ? "Upcoming live sessions section" : `${title} section`}
    >
      <div className="pt-3 pb-1">
        <h2
          id={effectiveSectionId}
          aria-level={2}
          className={getHeadingClassName(isUpcoming, headingSizeClassName)}
        >
          {displayTitle}
        </h2>
      </div>

      {loading && <WideCardSkeleton count={isUpcoming ? 3 : 4} title={displayTitle} />}

      {!loading && (
        <LivePlaylistDesktopBlock
          streams={allStreams}
          isUpcoming={isUpcoming}
          loading={loading}
          enableSuspense={enableSuspense}
          displayTitle={displayTitle}
          emptyMessage={emptyMessage}
          title={title}
          loadingLabel={loadingLabel}
          setReminderText={setReminderText}
          reminderSetText={reminderSetText}
          sectionTitle={displayTitle}
          sectionTitleCt={ctSectionTitle}
          influencerId={influencerId}
          reminderService={reminderService}
          onScrollEnd={handleScrollEnd}
          isFetchingMore={isFetchingMore}
          hasMore={Boolean(nextUrl)}
        />
      )}

      {!loading && (
        <LivePlaylistMobileBlock
          streams={allStreams}
          isUpcoming={isUpcoming}
          loading={loading}
          emptyMessage={emptyMessage}
          title={title}
          loadingLabel={loadingLabel}
          showShowMoreOnMobile={showShowMoreOnMobile}
          visibleCount={visibleCount}
          handleShowMore={handleShowMore}
          handleShowLess={handleShowLess}
          initialVisibleCount={effectiveIncrement}
          sectionId={mobileSectionId}
          showMobileList={showMobileList}
          setReminderText={setReminderText}
          reminderSetText={reminderSetText}
          sectionTitle={displayTitle}
          sectionTitleCt={ctSectionTitle}
          influencerId={influencerId}
          reminderService={reminderService}
          onScrollEnd={handleScrollEnd}
          isFetchingMore={isFetchingMore}
          isFetchingShowMore={isFetchingShowMore}
          hasMore={Boolean(nextUrl)}
        />
      )}
    </section>
  );
}
